import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth0 } from '@auth0/auth0-react'
import { MapContainer, Marker, Popup, TileLayer, Circle, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { LuBedDouble, LuBath, LuBuilding, LuGitCompare, LuHeart, LuHouse, LuLeaf, LuRuler, LuBell, LuX } from 'react-icons/lu'
import { BsHouses } from 'react-icons/bs'
import { PiWarehouse } from 'react-icons/pi'
import { MdCabin } from 'react-icons/md'
import PropertyCard from '../components/PropertyCard.jsx'
import ListingsAdPlaceholder from '../components/ListingsAdPlaceholder.jsx'
import LoadingIndicator from '../components/LoadingIndicator.jsx'
import PartnerAdvertModal from '../components/PartnerAdvertModal.jsx'
import { searchProperties } from '../services/searchService.js'
import { recordServiceLead } from '../services/inboxService.js'
import { track } from '../services/mixpanel.js'
import { getPlaceSuggestions } from '../services/placesService.js'
import { formatPriceWithQualifier } from '../utils/formatters.js'
import { useListingsPreferences } from '../context/ListingsPreferencesContext.jsx'
import { useSavedProperties } from '../hooks/useSavedProperties.js'
import { useComparison } from '../context/ComparisonContext.jsx'
import { useToast } from '../hooks/useToast.js'
import 'leaflet/dist/leaflet.css'

const formatPriceDisplay = (value) => {
  if (!value) return ''
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return ''
  return `£${numeric.toLocaleString('en-GB')}`
}

const propertyTypeOptions = ['Detached', 'Semi-detached', 'Terraced', 'Flat', 'Bungalow', 'Land', 'Park home']
const radiusOptions = ['0.25', '0.5', '1', '2', '3', '5', '10', '25', '50', '100']
const epcRatingOptions = ['A', 'B', 'C', 'D', 'E', 'F', 'G']
const tenureOptions = [
  { value: 'freehold', label: 'Freehold' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'shared_ownership', label: 'Shared ownership' },
]
const LAST_LOCATION_KEY = 'hh:last-search-location'
const propertyTypeIcons = {
  Detached: LuHouse,
  'Semi-detached': BsHouses,
  Terraced: 'cluster',
  Flat: LuBuilding,
  Bungalow: PiWarehouse,
  Land: LuLeaf,
  'Park home': MdCabin,
}

const mapMarkerIcon = L.divIcon({
  className: 'map-marker-wrapper',
  html: '<span class="map-marker"></span>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
})

const cityCoordinates = {
  London: [51.5074, -0.1278],
  Manchester: [53.4808, -2.2426],
  Bristol: [51.4545, -2.5879],
  'Brighton & Hove': [50.8225, -0.1372],
  Glasgow: [55.8642, -4.2518],
  Edinburgh: [55.9533, -3.1883],
  Leeds: [53.8008, -1.5491],
  Birmingham: [52.4862, -1.8904],
  Bath: [51.3813, -2.3572],
  Oxford: [51.752, -1.2577],
  'Lake District': [54.4609, -3.0886],
}

const getStoredLocation = () => {
  if (typeof window === 'undefined') return ''
  try {
    return localStorage.getItem(LAST_LOCATION_KEY) || ''
  } catch {
    return ''
  }
}

const normalizeTenure = (value) =>
  value ? value.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '_') : ''

const getPosition = (property) => {
  const lat = typeof property.latitude === 'number' ? property.latitude : Number(property.latitude)
  const lng = typeof property.longitude === 'number' ? property.longitude : Number(property.longitude)

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return [lat, lng]
  }

  const base = cityCoordinates[property.city] || [54.5, -3]
  const seed = parseInt(property.id.replace(/\D/g, ''), 10) || 0
  const latOffset = ((seed % 5) - 2) * 0.01
  const lngOffset = ((Math.floor(seed / 5) % 5) - 2) * 0.01
  return [base[0] + latOffset, base[1] + lngOffset]
}

const inclusionFilters = [
  { id: 'newHome', label: 'New homes', includeKey: 'newHome', excludeKey: 'excludeNewHome' },
  { id: 'buyingScheme', label: 'Buying schemes', includeKey: 'buyingScheme', excludeKey: 'excludeBuyingScheme' },
  { id: 'auction', label: 'Auction listings', includeKey: 'auction', excludeKey: 'excludeAuction' },
  { id: 'retirement', label: 'Retirement homes', includeKey: 'isRetirement', excludeKey: 'excludeRetirement' },
]

const statusFilters = [
  { key: 'statusAvailable', label: 'Available', value: 'Available' },
  { key: 'statusUnderOffer', label: 'Under offer', value: 'Under offer' },
]

const DEFAULT_PAGE_SIZE = 12
const MAP_PAGE_SIZE = 200

// --- Map clustering helpers ---

const clusterIcon = (count) =>
  L.divIcon({
    className: 'map-cluster-wrapper',
    html: `<span class="map-cluster">${count}</span>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  })

function clusterRadius(zoom) {
  // Returns a geographic degree threshold that corresponds to ~50px at given zoom
  return 0.01 * Math.pow(2, 13 - Math.max(zoom, 4))
}

function computeClusters(positions, zoom) {
  const used = new Set()
  const clusters = []
  const radius = clusterRadius(zoom)
  positions.forEach((item, i) => {
    if (used.has(i)) return
    const cluster = { items: [item] }
    positions.forEach((other, j) => {
      if (i === j || used.has(j)) return
      const dlat = item.position[0] - other.position[0]
      const dlng = item.position[1] - other.position[1]
      if (Math.sqrt(dlat * dlat + dlng * dlng) < radius) {
        cluster.items.push(other)
        used.add(j)
      }
    })
    used.add(i)
    if (cluster.items.length > 1) {
      const avgLat = cluster.items.reduce((s, it) => s + it.position[0], 0) / cluster.items.length
      const avgLng = cluster.items.reduce((s, it) => s + it.position[1], 0) / cluster.items.length
      cluster.center = [avgLat, avgLng]
    } else {
      cluster.center = item.position
    }
    clusters.push(cluster)
  })
  return clusters
}

function isColocated(cluster) {
  if (cluster.items.length < 2) return false
  const [lat0, lng0] = cluster.items[0].position
  return cluster.items.every(({ position: [lat, lng] }) =>
    Math.abs(lat - lat0) < 0.00002 && Math.abs(lng - lng0) < 0.00002
  )
}

// Returns true only if all items in the cluster would still form a single cluster
// at the given zoom level (i.e. zooming can never separate them).
// Checking "any pair" was wrong: two sub-groups each internally close but
// far from each other would fire the check even though zooming would split them.
function wouldStillClusterAtZoom(cluster, zoom) {
  const subClusters = computeClusters(cluster.items, zoom)
  return subClusters.length === 1
}

// A cluster marker that zooms on click, or shows a card list on hover only when stuck at lowest zoom.
function ClusterMarker({ cluster, idx, zoom, map, suppressRef, listingLinkState, isInComparison, addToComparison, removeFromComparison, isFull }) {
  const markerRef = useRef(null)
  const timerRef = useRef(null)
  const mapMaxZoom = map.getMaxZoom()
  const effectiveMaxZoom = isFinite(mapMaxZoom) ? mapMaxZoom : 19
  // stuck = at max zoom, OR items are exactly colocated, OR zooming to max still won't separate them
  const stuck = zoom >= effectiveMaxZoom || isColocated(cluster) || wouldStillClusterAtZoom(cluster, effectiveMaxZoom)

  const clearClose = useCallback(() => clearTimeout(timerRef.current), [])
  const scheduleClose = useCallback(() => {
    timerRef.current = setTimeout(() => markerRef.current?.closePopup(), 400)
  }, [])

  useEffect(() => {
    if (!stuck) return
    const marker = markerRef.current
    if (!marker) return
    const onPopupOpen = () => {
      const el = marker.getPopup()?.getElement()
      if (!el) return
      el.addEventListener('mouseenter', clearClose)
      el.addEventListener('mouseleave', scheduleClose)
    }
    marker.on('popupopen', onPopupOpen)
    return () => marker.off('popupopen', onPopupOpen)
  }, [stuck, clearClose, scheduleClose])

  return (
    <Marker
      ref={markerRef}
      key={`cluster-${idx}`}
      position={cluster.center}
      icon={clusterIcon(cluster.items.length)}
      eventHandlers={{
        mouseover: () => { if (stuck && !suppressRef.current) { clearClose(); markerRef.current?.openPopup() } },
        mouseout: () => { if (stuck) scheduleClose() },
        click: () => { if (!stuck) map.setView(cluster.center, map.getZoom() + 2) },
      }}
    >
      <Popup
        className="map-popup map-popup--cluster"
        autoPan={true}
        autoPanPadding={[12, 12]}
        closeButton={false}
      >
        <div className="map-popup__cluster-list">
          {cluster.items.map(({ property }) => (
            <div key={property.id} className="map-popup__card">
              <div className="map-popup__img-wrap">
                <img src={property.heroImage} alt={property.title} loading="lazy" />
              </div>
              <div className="map-popup__body">
                <p className="map-popup__price">{formatPriceWithQualifier(property.price, property.priceQualifier)}</p>
                <strong className="map-popup__title">{property.title}</strong>
                <p className="map-popup__location">{[property.location, property.city].filter(Boolean).join(', ')}</p>
                <div className="map-popup__stats">
                  <span><LuBedDouble aria-hidden="true" /> {property.beds}</span>
                  <span><LuBath aria-hidden="true" /> {property.baths}</span>
                </div>
                <Link to={`/property/${property.id}`} className="map-popup__view-btn" state={listingLinkState}>
                  View details
                </Link>
                <button
                  type="button"
                  className={`map-popup__compare-btn${isInComparison(property.id) ? ' is-comparing' : ''}`}
                  disabled={!isInComparison(property.id) && isFull}
                  onClick={() => isInComparison(property.id) ? removeFromComparison(property.id) : addToComparison(property.id)}
                  aria-label={isInComparison(property.id) ? 'Remove from compare' : isFull ? 'Comparison full' : 'Compare'}
                >
                  <LuGitCompare aria-hidden="true" />
                  {isInComparison(property.id) ? 'Remove' : isFull ? 'Full' : 'Compare'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Popup>
    </Marker>
  )
}

// A marker that opens its popup on hover, keeping it open while the mouse is on the popup
function HoverMarker({ position, icon, suppressRef, children }) {
  const markerRef = useRef(null)
  const timerRef = useRef(null)

  const clearClose = useCallback(() => clearTimeout(timerRef.current), [])

  const scheduleClose = useCallback(() => {
    timerRef.current = setTimeout(() => markerRef.current?.closePopup(), 400)
  }, [])

  useEffect(() => {
    const marker = markerRef.current
    if (!marker) return
    const onPopupOpen = () => {
      const el = marker.getPopup()?.getElement()
      if (!el) return
      el.addEventListener('mouseenter', clearClose)
      el.addEventListener('mouseleave', () => marker.closePopup())
    }
    marker.on('popupopen', onPopupOpen)
    return () => marker.off('popupopen', onPopupOpen)
  }, [clearClose])

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      eventHandlers={{
        mouseover: () => { if (!suppressRef.current) { clearClose(); markerRef.current?.openPopup() } },
        mouseout: scheduleClose,
        click: () => {
          const marker = markerRef.current
          if (!marker) return
          marker.isPopupOpen() ? marker.closePopup() : marker.openPopup()
        },
      }}
    >
      {children}
    </Marker>
  )
}

function ClusteredMarkers({ mappedPositions, listingLinkState, isInComparison, addToComparison, removeFromComparison, isFull, isSaved, onSave }) {
  const map = useMap()
  const [zoom, setZoom] = useState(() => map.getZoom())
  const suppressRef = useRef(false)
  const suppressTimer = useRef(null)

  const suppressHover = useCallback((ms) => {
    suppressRef.current = true
    clearTimeout(suppressTimer.current)
    suppressTimer.current = setTimeout(() => { suppressRef.current = false }, ms)
  }, [])

  useMapEvents({
    zoomstart: () => { map.closePopup(); suppressHover(1200) },
    zoomend: () => { setZoom(map.getZoom()); suppressHover(700) },
  })

  const clusters = useMemo(
    () => computeClusters(mappedPositions, zoom),
    [mappedPositions, zoom],
  )

  return clusters.map((cluster, idx) => {
    if (cluster.items.length > 1) {
      return (
        <ClusterMarker
          key={`cluster-${idx}`}
          cluster={cluster}
          idx={idx}
          zoom={zoom}
          map={map}
          suppressRef={suppressRef}
          listingLinkState={listingLinkState}
          isInComparison={isInComparison}
          addToComparison={addToComparison}
          removeFromComparison={removeFromComparison}
          isFull={isFull}
        />
      )
    }
    const { property, position } = cluster.items[0]
    return (
      <HoverMarker key={property.id} position={position} icon={mapMarkerIcon} suppressRef={suppressRef}>
        <Popup className="map-popup" autoPan={true} autoPanPadding={[12, 12]} closeButton={false}>
          <div className="map-popup__card">
            <div className="map-popup__img-wrap">
              <img src={property.heroImage} alt={property.title} loading="lazy" />
              <span className="map-popup__tag">{property.propertyType?.replace('_', ' ').toUpperCase()}</span>
              <button
                type="button"
                className={`map-popup__save${isSaved(property.id) ? ' is-saved' : ''}`}
                onClick={(e) => { e.stopPropagation(); onSave(e, property.id) }}
                aria-label={isSaved(property.id) ? 'Remove from saved' : 'Save property'}
              >
                <LuHeart aria-hidden="true" style={{ fill: isSaved(property.id) ? 'currentColor' : 'none' }} />
              </button>
            </div>
            <div className="map-popup__body">
              <p className="map-popup__price">
                {formatPriceWithQualifier(property.price, property.priceQualifier)}
              </p>
              <strong className="map-popup__title">{property.title}</strong>
              <p className="map-popup__location">
                {[property.location, property.city].filter(Boolean).join(', ')}
              </p>
              <div className="map-popup__stats">
                <span><LuBedDouble aria-hidden="true" /> {property.beds} bd</span>
                <span><LuBath aria-hidden="true" /> {property.baths} ba</span>
                {property.area && <span><LuRuler aria-hidden="true" /> {property.area}</span>}
              </div>
              <Link to={`/property/${property.id}`} className="map-popup__view-btn" state={listingLinkState}>
                View details
              </Link>
              <button
                type="button"
                className={`map-popup__compare-btn${isInComparison(property.id) ? ' is-comparing' : ''}`}
                disabled={!isInComparison(property.id) && isFull}
                onClick={() => isInComparison(property.id) ? removeFromComparison(property.id) : addToComparison(property.id)}
                aria-label={isInComparison(property.id) ? 'Remove from comparison' : isFull ? 'Comparison full' : 'Compare'}
              >
                <LuGitCompare aria-hidden="true" />
                {isInComparison(property.id) ? 'Remove from compare' : isFull ? 'Comparison full' : 'Compare'}
              </button>
            </div>
          </div>
        </Popup>
      </HoverMarker>
    )
  })
}

const ListingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, user } = useAuth0()
  const { showToast } = useToast()
  const [saveSearchOpen, setSaveSearchOpen] = useState(false)
  const [saveSearchName, setSaveSearchName] = useState('')
  const [saveSearchStatus, setSaveSearchStatus] = useState(null) // null | 'saving' | 'error'

  const filters = useMemo(
    () => ({
      location: searchParams.get('location') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      propertyTypes: (searchParams.get('types') || '')
        .split(',')
        .map((type) => type.trim())
        .filter(Boolean),
      minBeds: searchParams.get('minBeds') || '',
      minBaths: searchParams.get('minBaths') || '',
      hasParking: searchParams.get('parking') === 'true',
      hasGarden: searchParams.get('garden') === 'true',
      hasGarage: searchParams.get('garage') === 'true',
      tenure: (searchParams.get('tenure') || '')
        .split(',')
        .map((item) => item.trim())
        .map(normalizeTenure)
        .filter((item) => item && item !== 'any'),
      isRetirement: searchParams.get('retirement') === 'true',
      newHome: searchParams.get('newHome') === 'true',
      buyingScheme: searchParams.get('buyingScheme') === 'true',
      auction: searchParams.get('auction') === 'true',
      excludeNewHome: searchParams.get('excludeNewHome') === 'true',
      excludeRetirement: searchParams.get('excludeRetirement') === 'true',
      excludeBuyingScheme: searchParams.get('excludeBuyingScheme') === 'true',
      excludeAuction: searchParams.get('excludeAuction') === 'true',
      radius: searchParams.get('radius') || '',
      statusFilters: (searchParams.get('statuses') || '')
        .split(',')
        .map((status) => status.trim())
        .filter(Boolean),
      sort: searchParams.get('sort') || 'price-desc',
      page: Number(searchParams.get('page')) || 1,
      minEpcRating: searchParams.get('minEpc') || '',
      maxCouncilTaxBand: searchParams.get('maxCtb') || '',
    }),
    [searchParams],
  )

  const hasActiveFilters = useMemo(() => {
    const { sort: _sort, page: _page, ...rest } = filters
    return Object.values(rest).some((value) => (Array.isArray(value) ? value.length > 0 : Boolean(value)))
  }, [filters])

  const [filterForm, setFilterForm] = useState(filters)
  const filterFormRef = useRef(filters)
  const locationRoute = useLocation()
  const navigate = useNavigate()
  const {
    preferences: { viewMode, scrollPosition },
    setViewMode,
    setScrollPosition,
  } = useListingsPreferences()
  const hasRestoredScroll = useRef(false)
  const allowScrollCapture = useRef(false)
  const { isSaved, toggleSaved } = useSavedProperties()
  const { isInComparison, addToComparison, removeFromComparison, isFull } = useComparison()
  const listingLinkState = useMemo(() => ({ fromListings: true }), [])
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [filtersCollapsed, setFiltersCollapsed] = useState(false)
  const [isMobileFilters, setIsMobileFilters] = useState(false)
  const [results, setResults] = useState([])
  const [meta, setMeta] = useState({
    page: filters.page || 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    location: null,
    suggestions: {},
  })
  const [lastLocation, setLastLocation] = useState(getStoredLocation)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  const [promotedPartners, setPromotedPartners] = useState([])
  const [advertPartner, setAdvertPartner] = useState(null)
  const [advertForm, setAdvertForm] = useState({ name: '', email: '', phone: '' })
  const [advertSubmitting, setAdvertSubmitting] = useState(false)
  const [advertError, setAdvertError] = useState(null)
  const [advertSuccess, setAdvertSuccess] = useState(false)
  const [advertSuccessMessage, setAdvertSuccessMessage] = useState(null)
  const [locationSuggestions, setLocationSuggestions] = useState([])
  const [suggestionsVisible, setSuggestionsVisible] = useState(false)
  const locationInputRef = useRef(null)
  const locationFetchRef = useRef(0)
  const suppressLocationFetchRef = useRef(false)
  const lastLocationRef = useRef(lastLocation)
  const needsLocationInitially = Boolean(filters.radius || filters.sort === 'distance-asc')
  const initialLocation = filters.location || (needsLocationInitially ? lastLocation : '') || ''
  const [locationInputValue, setLocationInputValue] = useState(initialLocation)
  const [confirmedLocation, setConfirmedLocation] = useState(initialLocation)
  const [isEditingLocation, setIsEditingLocation] = useState(false)
  const isEditingLocationRef = useRef(false)
  const [locationHighlightIndex, setLocationHighlightIndex] = useState(-1)
  const pendingScrollRef = useRef(false)
  const pendingFilterSyncRef = useRef(null)
  const resetScrollOnLoadRef = useRef(locationRoute.state?.resetScroll ?? false)

  const requestLoginForSave = useCallback(() => {
    if (typeof window === 'undefined') {
      loginWithRedirect?.()
      return
    }
    loginWithRedirect?.({
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
    })
  }, [loginWithRedirect])

  const handleAdLearnMore = useCallback((partner) => {
    setAdvertPartner(partner)
    setAdvertForm({
      name: user?.name || '',
      email: user?.email || '',
      phone: '',
    })
    setAdvertError(null)
    setAdvertSuccess(false)
    setAdvertSuccessMessage(null)
  }, [user])

  const handleAdvertFormChange = useCallback((field, value) => {
    setAdvertForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleAdvertCallback = useCallback(async (e) => {
    e.preventDefault()
    setAdvertSubmitting(true)
    setAdvertError(null)
    try {
      const token = isAuthenticated ? await getAccessTokenSilently() : null
      await recordServiceLead(
        {
          orgId: advertPartner?.id,
          partnerName: advertPartner?.name,
          serviceType: advertPartner?.businessType || 'advertiser',
          contactName: advertForm.name,
          contactEmail: advertForm.email,
          contactPhone: advertForm.phone,
        },
        { token },
      )
      setAdvertSuccess(true)
    } catch (err) {
      if (err.status === 409) {
        setAdvertSuccessMessage(`You've already requested a callback from ${advertPartner?.name}. They will be in touch with you.`)
        setAdvertSuccess(true)
      } else {
        setAdvertError('Unable to submit your request. Please try again.')
      }
    } finally {
      setAdvertSubmitting(false)
    }
  }, [advertPartner, advertForm, isAuthenticated, getAccessTokenSilently])

  const handleListSaveToggle = useCallback(
    (event, propertyId) => {
      event.preventDefault()
      if (!isAuthenticated) {
        requestLoginForSave()
        return
      }
      toggleSaved(propertyId)
    },
    [isAuthenticated, requestLoginForSave, toggleSaved],
  )

useEffect(() => {
  if (locationRoute.state?.resetScroll) {
    navigate('.', {
      replace: true,
      state: { ...locationRoute.state, resetScroll: false },
    })
  }
}, [locationRoute, navigate])

useEffect(() => {
  setFilterForm(filters)
}, [filters])

useEffect(() => {
  isEditingLocationRef.current = isEditingLocation
}, [isEditingLocation])

useEffect(() => {
  lastLocationRef.current = lastLocation
}, [lastLocation])

useEffect(() => {
  if (isEditingLocationRef.current) return
  const needsLocation = Boolean(filters.radius || filters.sort === 'distance-asc')
  const nextLocation = filters.location || (needsLocation ? lastLocationRef.current : '')
  setConfirmedLocation(nextLocation)
  setLocationInputValue(nextLocation)
}, [filters.location, filters.radius, filters.sort])

useEffect(() => {
  const trimmed = (confirmedLocation || '').trim()
  if (!trimmed) return
  setLastLocation(trimmed)
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(LAST_LOCATION_KEY, trimmed)
    } catch {
      /* ignore write errors */
    }
  }
}, [confirmedLocation])

  useEffect(() => {
    if (suppressLocationFetchRef.current) {
      suppressLocationFetchRef.current = false
      return
    }
    if (!isEditingLocation) {
      return
    }
    if (!locationInputValue || locationInputValue.trim().length < 2) {
      setLocationSuggestions([])
      setSuggestionsVisible(false)
      return
    }
    const fetchId = ++locationFetchRef.current
    const timeout = setTimeout(() => {
      const snapshot = fetchId
      getPlaceSuggestions(locationInputValue.trim(), { limit: 6 })
        .then((items) => {
          if (snapshot !== locationFetchRef.current) return
          setLocationSuggestions(items)
          setSuggestionsVisible(items.length > 0)
        })
        .catch(() => {
          if (snapshot !== locationFetchRef.current) return
          setLocationSuggestions([])
          setSuggestionsVisible(false)
        })
    }, 150)
    return () => clearTimeout(timeout)
  }, [locationInputValue, isEditingLocation])

  useEffect(() => {
    setLocationHighlightIndex(-1)
  }, [locationSuggestions])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!locationInputRef.current) return
      if (!locationInputRef.current.contains(event.target)) {
        setSuggestionsVisible(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const scrollResultsToTop = useCallback(() => {
    if (typeof window === 'undefined') return
    if (window.scrollY < 300) return
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
    setScrollPosition(0)
  }, [setScrollPosition])

  const canUseDistanceFilters = useMemo(
    () => Boolean((confirmedLocation || '').trim() || (lastLocation || '').trim()),
    [confirmedLocation, lastLocation],
  )

  const queryFilters = useMemo(() => {
    const trimmed = (confirmedLocation || '').trim()
    const fallback = (lastLocation || '').trim()
    const resolvedLocation = trimmed || fallback
    const needsLocation = Boolean(filters.radius) || filters.sort === 'distance-asc'
    return {
      ...filters,
      location: needsLocation ? resolvedLocation : '',
    }
  }, [filters, confirmedLocation, lastLocation])
  const queryKey = useMemo(() => JSON.stringify(queryFilters), [queryFilters])

  useEffect(() => {
    const parsedFilters = JSON.parse(queryKey)
    parsedFilters.suggestions = 'true'
    if (viewMode === 'map') {
      parsedFilters.pageSize = MAP_PAGE_SIZE
    }
    if (pendingScrollRef.current) {
      scrollResultsToTop()
      pendingScrollRef.current = false
    }

    const controller = new AbortController()
    let active = true
    setLoading(true)
    setError(null)

    searchProperties(parsedFilters, { signal: controller.signal })
      .then((payload) => {
        if (!active) return
        const resultsData = payload.data ?? []
        setResults(resultsData)
        track('search_performed', {
          query_location: parsedFilters.location || null,
          result_count: resultsData.length,
          min_price: parsedFilters.minPrice || null,
          max_price: parsedFilters.maxPrice || null,
          min_beds: parsedFilters.minBeds || null,
          property_types: parsedFilters.types || null,
        })
        setMeta(
          payload.meta ?? {
            page: parsedFilters.page || 1,
            pageSize: DEFAULT_PAGE_SIZE,
            total: payload.data?.length ?? 0,
            totalPages: 1,
            location: null,
            suggestions: {},
          },
        )
      })
      .catch((err) => {
        if (err.name === 'AbortError' || !active) return
        console.error('Unable to load listings', err)
        setError(err.message || 'Unable to load listings right now.')
      })
      .finally(() => {
        if (!active) return
        setLoading(false)
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [queryKey, viewMode, scrollResultsToTop, retryCount])

  useEffect(() => {
    hasRestoredScroll.current = false
    allowScrollCapture.current = false
  }, [queryKey])

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''
    const url = new URL(`${API_BASE}/.netlify/functions/promoted-partners`, window.location.origin)
    const location = (confirmedLocation || '').trim()
    if (location) url.searchParams.set('location', location)

    let active = true
    fetch(url.toString())
      .then((res) => res.json())
      .then((payload) => {
        if (!active) return
        setPromotedPartners(Array.isArray(payload.data) ? payload.data : [])
      })
      .catch(() => {
        if (active) setPromotedPartners([])
      })
    return () => {
      active = false
    }
  }, [confirmedLocation])

  useEffect(() => {
    if (hasRestoredScroll.current) return
    if (loading) return
    const timer = setTimeout(() => {
      const targetPosition = resetScrollOnLoadRef.current ? 0 : scrollPosition || 0
      window.scrollTo({
        top: targetPosition,
        behavior: 'auto',
      })
      resetScrollOnLoadRef.current = false
      allowScrollCapture.current = true
      hasRestoredScroll.current = true
    }, 350)
    return () => clearTimeout(timer)
  }, [loading, scrollPosition, viewMode, results.length])

  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!allowScrollCapture.current) {
        return
      }
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(() => {
        setScrollPosition(window.scrollY || window.pageYOffset || 0)
        ticking = false
      })
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [setScrollPosition])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mediaQuery = window.matchMedia('(max-width: 520px)')
    const applyMatch = (matches) => {
      setIsMobileFilters(matches)
      if (!matches) {
        setFiltersCollapsed(false)
      }
    }
    const handleChange = (event) => applyMatch(event.matches)
    applyMatch(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const mappedPositions = useMemo(
    () => results.map((property) => ({ property, position: getPosition(property) })),
    [results],
  )

  const mapCenter = useMemo(() => {
    if (meta.location && typeof meta.location.latitude === 'number' && typeof meta.location.longitude === 'number') {
      return [meta.location.latitude, meta.location.longitude]
    }
    return [54.5, -3]
  }, [meta.location])

  const mapZoom = useMemo(() => {
    const radius = meta.location?.radius
    if (typeof radius === 'number') {
      if (radius <= 1) return 12
      if (radius <= 3) return 11
      if (radius <= 5) return 10
      if (radius <= 10) return 9
      if (radius <= 25) return 8
      if (radius <= 50) return 7
      if (radius <= 100) return 6
    }
    return 6
  }, [meta.location])

  const resultsWithAd = useMemo(() => {
    if (results.length < 4) return results
    const currentPage = meta.page || 1
    // Collect insertion positions (every 6 results)
    const insertPositions = []
    for (let i = 6; i <= results.length; i += 6) {
      insertPositions.push(i)
    }
    if (!insertPositions.length) insertPositions.push(Math.min(4, results.length))
    const stitched = [...results]
    // Insert ad markers in reverse order to preserve earlier indices
    for (let s = insertPositions.length - 1; s >= 0; s--) {
      const slotNum = s
      // Offset partner index by page so successive pages cycle through partners
      const partnerIndex = (currentPage - 1) * insertPositions.length + slotNum
      stitched.splice(insertPositions[s], 0, {
        __ad: true,
        id: `ad-slot-${currentPage}-${slotNum}`,
        partnerIndex,
      })
    }
    return stitched
  }, [results, meta.page])

  useEffect(() => {
    filterFormRef.current = filterForm
  }, [filterForm])

  useEffect(() => {
    const trimmed = (confirmedLocation || '').trim()
    const current = (filters.location || '').trim()
    if (trimmed === current) return
    const next = { ...filterFormRef.current, location: trimmed, page: 1 }
    pendingFilterSyncRef.current = { formState: next, scrollToTop: true }
    setFilterForm(next)
  }, [confirmedLocation, filters.location])

  const updateFilterForm = useCallback((key, value, { resetPage = key !== 'page', immediate = false } = {}) => {
    setFilterForm((prev) => {
      const next = { ...prev, [key]: value }
      const contradictoryFilters = {
        newHome: 'excludeNewHome',
        buyingScheme: 'excludeBuyingScheme',
        auction: 'excludeAuction',
        isRetirement: 'excludeRetirement',
        excludeNewHome: 'newHome',
        excludeBuyingScheme: 'buyingScheme',
        excludeAuction: 'auction',
        excludeRetirement: 'isRetirement',
      }
      const opposingKey = contradictoryFilters[key]
      if (opposingKey && value === true) {
        next[opposingKey] = false
      }
      if (resetPage) {
        next.page = 1
      }
      if (key === 'location' && !immediate) {
        return next
      }
      pendingFilterSyncRef.current = { formState: next, scrollToTop: true }
      return next
    })
  }, [])

  useEffect(() => {
    if (canUseDistanceFilters) return
    if (filterForm.radius) {
      updateFilterForm('radius', '')
    }
    if (filterForm.sort === 'distance-asc') {
      updateFilterForm('sort', 'price-desc', { resetPage: false })
    }
  }, [canUseDistanceFilters, filterForm.radius, filterForm.sort, updateFilterForm])

  
  const suggestionConfigs = useMemo(() => {
    const serverSuggestions = meta.suggestions || {}
    return Object.entries(serverSuggestions).map(([id, payload]) => {
      const label = payload?.label || 'Adjust filters'
      const count = typeof payload?.count === 'number' ? payload.count : 0
      const nextValue = payload?.nextValue
      const hideCount = Boolean(payload?.hideCount)
      const apply = () => {
        switch (id) {
          case 'radius':
            return updateFilterForm('radius', nextValue != null ? String(nextValue) : '')
          case 'maxPrice':
            return updateFilterForm('maxPrice', nextValue != null ? String(nextValue) : '')
          case 'minPrice':
            return updateFilterForm('minPrice', nextValue != null ? String(nextValue) : '')
          case 'clearMinPrice':
            return updateFilterForm('minPrice', '')
          case 'minBeds':
            return updateFilterForm('minBeds', nextValue != null ? String(nextValue) : '')
          case 'minBaths':
            return updateFilterForm('minBaths', nextValue != null ? String(nextValue) : '')
          case 'national':
            return updateFilterForm('radius', '')
          default:
            return null
        }
      }
      return { id, label, count, hideCount, apply }
    })
  }, [meta.suggestions, updateFilterForm])

  const renderSuggestions = () => {
    if (!suggestionConfigs.length) return null
    const options = suggestionConfigs.filter((cfg) => (cfg.count || 0) > 0)
    if (!options.length) return null
    return (
      <div className="listings-suggestions">
        <p className="listings-suggestions__title">Try adjusting your filters:</p>
        <div className="listings-suggestions__grid">
          {options.map((option) => (
            <button key={option.id} type="button" className="ghost-button" onClick={option.apply}>
              {option.label}
              {!option.hideCount && ` (${option.count} homes)`}
            </button>
          ))}
        </div>
      </div>
    )
  }
  useEffect(() => {
    const pending = pendingFilterSyncRef.current
    if (!pending) return
    pendingFilterSyncRef.current = null
    if (pending.scrollToTop) {
      pendingScrollRef.current = true
    }
    const formState = pending.formState
    const params = new URLSearchParams()

    if (formState.location) params.set('location', formState.location)
    if (formState.minPrice) params.set('minPrice', formState.minPrice)
    if (formState.maxPrice) params.set('maxPrice', formState.maxPrice)
    if (formState.propertyTypes?.length) {
      params.set('types', formState.propertyTypes.join(','))
    }
    if (formState.minBeds) params.set('minBeds', formState.minBeds)
    if (formState.minBaths) params.set('minBaths', formState.minBaths)
    if (formState.hasParking) params.set('parking', 'true')
    if (formState.hasGarden) params.set('garden', 'true')
    if (formState.hasGarage) params.set('garage', 'true')
    if (formState.tenure?.length) params.set('tenure', formState.tenure.join(','))
    if (formState.isRetirement) params.set('retirement', 'true')
    if (formState.newHome) params.set('newHome', 'true')
    if (formState.buyingScheme) params.set('buyingScheme', 'true')
    if (formState.auction) params.set('auction', 'true')
    if (formState.excludeNewHome) params.set('excludeNewHome', 'true')
    if (formState.excludeRetirement) params.set('excludeRetirement', 'true')
    if (formState.excludeBuyingScheme) params.set('excludeBuyingScheme', 'true')
    if (formState.excludeAuction) params.set('excludeAuction', 'true')
    if (formState.radius) params.set('radius', formState.radius)
    if (formState.statusFilters?.length) params.set('statuses', formState.statusFilters.join(','))
    if (formState.page && formState.page > 1) params.set('page', String(formState.page))
    if (formState.sort) params.set('sort', formState.sort)
    if (formState.minEpcRating) params.set('minEpc', formState.minEpcRating)
    if (formState.maxCouncilTaxBand) params.set('maxCtb', formState.maxCouncilTaxBand)
    setSearchParams(params)
  }, [filterForm, setSearchParams])

  const handlePageChange = (nextPage) => {
    const target = Math.max(1, Math.min(nextPage, meta.totalPages || 1))
    updateFilterForm('page', target, { resetPage: false })
  }

  const handleSaveSearch = async (event) => {
    event.preventDefault()
    if (!isAuthenticated) {
      loginWithRedirect()
      return
    }
    setSaveSearchStatus('saving')
    try {
      const token = await getAccessTokenSilently()
      const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''
      const name = saveSearchName.trim() || (filterForm.location ? `Homes in ${filterForm.location}` : 'My search')
      const res = await fetch(`${API_BASE}/.netlify/functions/saved-searches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name,
          location: filterForm.location || null,
          minPrice: filterForm.minPrice ? Number(filterForm.minPrice) : null,
          maxPrice: filterForm.maxPrice ? Number(filterForm.maxPrice) : null,
          propertyTypes: filterForm.propertyTypes || [],
          minBeds: filterForm.minBeds ? Number(filterForm.minBeds) : null,
          minBaths: filterForm.minBaths ? Number(filterForm.minBaths) : null,
          hasParking: filterForm.hasParking || null,
          hasGarden: filterForm.hasGarden || null,
          hasGarage: filterForm.hasGarage || null,
          radius: filterForm.radius ? Number(filterForm.radius) : null,
          tenure: filterForm.tenure || [],
          isRetirement: filterForm.isRetirement || null,
          isNewHome: filterForm.newHome || null,
          isAuction: filterForm.auction || null,
          hasBuyingScheme: filterForm.buyingScheme || null,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setSaveSearchOpen(false)
      setSaveSearchStatus(null)
      setSaveSearchName('')
      showToast(`"${name}" saved — manage it in your Buyer Hub.`, { tone: 'positive', duration: 5000 })
    } catch {
      setSaveSearchStatus('error')
    }
  }

  const cancelLocationSuggestions = () => {
    locationFetchRef.current += 1
  }

  const handleLocationSuggestionSelect = (suggestion) => {
    cancelLocationSuggestions()
    suppressLocationFetchRef.current = true
    setLocationInputValue(suggestion.value)
    setConfirmedLocation(suggestion.value)
    setIsEditingLocation(false)
    updateFilterForm('location', suggestion.value, { immediate: true })
    setSuggestionsVisible(false)
    setLocationSuggestions([])
    setLocationHighlightIndex(-1)
  }

  const listingTitle = searchParams.get('location')
    ? `Properties for Sale in ${searchParams.get('location')} — Keyzee`
    : 'Properties for Sale — Browse Owner-Listed Homes | Keyzee'
  const listingDesc = searchParams.get('location')
    ? `Browse owner-listed homes for sale in ${searchParams.get('location')}. No estate agents, direct from sellers on Keyzee.`
    : 'Search thousands of owner-listed properties for sale across the UK. Filter by location, price, bedrooms and more on Keyzee.'

  return (
    <>
    <Helmet>
      <title>{listingTitle}</title>
      <meta name="description" content={listingDesc} />
      <meta property="og:title" content={listingTitle} />
      <meta property="og:description" content={listingDesc} />
      <meta property="og:type" content="website" />
    </Helmet>
    <section className="listings-shell">
      <header className="listings-header">
        <div>
          <p className="search-hero__eyebrow">Search results</p>
          <h1>
            {meta.total} {meta.total === 1 ? 'home' : 'homes'} found
          </h1>
        </div>
        <div className="listings-header__controls">
          <div className="view-toggle" role="group" aria-label="Listing layout options">
            <button
              type="button"
              className={viewMode === 'cards' ? 'active' : undefined}
              onClick={() => setViewMode('cards')}
              style={{ whiteSpace: 'nowrap' }}
            >
              Card view
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : undefined}
              onClick={() => setViewMode('list')}
              style={{ whiteSpace: 'nowrap' }}
            >
              List view
            </button>
            <button
              type="button"
              className={viewMode === 'map' ? 'active' : undefined}
              onClick={() => setViewMode('map')}
              style={{ whiteSpace: 'nowrap' }}
            >
              Map view
            </button>
          </div>

          {viewMode !== 'map' && (
            <div className="sort-control">
              <label>
                <select value={filterForm.sort || 'price-desc'} onChange={(event) => updateFilterForm('sort', event.target.value)}>
                  <option value="price-desc">Price (high to low)</option>
                  <option value="price-asc">Price (low to high)</option>
                  <option value="beds-desc">Beds (high to low)</option>
                  <option value="beds-asc">Beds (low to high)</option>
                  <option value="newest">Newest listings</option>
                  {canUseDistanceFilters && (filterForm.radius || filterForm.location || filterForm.sort === 'distance-asc') && (
                    <option value="distance-asc">Distance (near to far)</option>
                  )}
                </select>
              </label>
            </div>
          )}
        </div>
      </header>

      <form
        className={`listings-filters ${showAdvanced ? 'expanded' : ''}${filtersCollapsed ? ' is-collapsed' : ''}`}
        onSubmit={(event) => event.preventDefault()}
        data-collapsed={filtersCollapsed || undefined}
      >
        <div className="main-filter-row">
          <div className="listings-filters__grid">
            <label className="location-autocomplete" ref={locationInputRef}>
              <input
                type="text"
                value={locationInputValue}
                onChange={(event) => {
                  const nextValue = event.target.value
                  setIsEditingLocation(true)
                  cancelLocationSuggestions()
                  if (!nextValue.trim()) {
                    setConfirmedLocation('')
                  }
                  setLocationInputValue(nextValue)
                }}
                placeholder="e.g. SW3"
                onFocus={() => setSuggestionsVisible(locationSuggestions.length > 0)}
                onKeyDown={(event) => {
                  if (!locationSuggestions.length) {
                    return
                  }
                  if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    setSuggestionsVisible(true)
                    setLocationHighlightIndex((prev) => {
                      if (prev < 0) return 0
                      return (prev + 1) % locationSuggestions.length
                    })
                    return
                  }
                  if (event.key === 'ArrowUp') {
                    event.preventDefault()
                    setSuggestionsVisible(true)
                    setLocationHighlightIndex((prev) => {
                      if (prev < 0) return locationSuggestions.length - 1
                      return (prev - 1 + locationSuggestions.length) % locationSuggestions.length
                    })
                    return
                  }
                  if (event.key === 'Enter') {
                    if (!suggestionsVisible) return
                    const selected =
                      locationHighlightIndex >= 0
                        ? locationSuggestions[locationHighlightIndex]
                        : locationSuggestions.length === 1
                          ? locationSuggestions[0]
                          : null
                    if (selected) {
                      event.preventDefault()
                      handleLocationSuggestionSelect(selected)
                    }
                    return
                  }
                  if (event.key === 'Escape') {
                    setSuggestionsVisible(false)
                    setLocationHighlightIndex(-1)
                  }
                }}
              />
              {suggestionsVisible && locationSuggestions.length > 0 && (
                <ul className="location-suggestions">
                  {locationSuggestions.map((suggestion, index) => (
                    <li
                      key={`${suggestion.type}-${suggestion.value}`}
                      onMouseDown={() => handleLocationSuggestionSelect(suggestion)}
                      className={locationHighlightIndex === index ? 'is-highlighted' : ''}
                    >
                      <span className="location-suggestions__label">{suggestion.label}</span>
                      {suggestion.type === 'postcode' && suggestion.city && (
                        <span className="location-suggestions__meta">{suggestion.city}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </label>
            <label>

              <select
                value={filterForm.radius}
                onChange={(event) => updateFilterForm('radius', event.target.value)}
                disabled={!canUseDistanceFilters}
                title={!canUseDistanceFilters ? 'Add a location to enable distance filters' : undefined}
              >
                <option value="">Any distance</option>
                {radiusOptions
                  .filter(Boolean)
                  .map((option) => (
                    <option key={option} value={option}>
                      {option} miles
                    </option>
                  ))}
              </select>
            </label>
            <div className="filter-group filter-group--range">
              <label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatPriceDisplay(filterForm.minPrice)}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '')
                    updateFilterForm('minPrice', digits)
                  }}
                  placeholder="Min price"
                  aria-label="Minimum price"
                />
              </label>
              <span className="filter-group__divider">to</span>
              <label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatPriceDisplay(filterForm.maxPrice)}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '')
                    updateFilterForm('maxPrice', digits)
                  }}
                  placeholder="Max price"
                  aria-label="Maximum price"
                />
              </label>
            </div>
            <div className="filter-group filter-group--range">
              <label >
                <select
                  aria-label="Minimum bedrooms"
                  value={filterForm.minBeds}
                  onChange={(event) => updateFilterForm('minBeds', event.target.value)}
                >
                  <option value="">Any beds</option>
                  {[1, 2, 3, 4, 5].map((count) => (
                    <option key={`beds-${count}`} value={count}>
                      {count} beds min
                    </option>
                  ))}
                  <option value="6">6+ beds</option>
                </select>
              </label>
              <label>
                <select
                  aria-label="Minimum bathrooms"
                  value={filterForm.minBaths}
                  onChange={(event) => updateFilterForm('minBaths', event.target.value)}
                >
                  <option value="">Any baths</option>
                  {[1, 2, 3, 4].map((count) => (
                    <option key={`baths-${count}`} value={count}>
                      {count} baths min
                    </option>
                  ))}
                  <option value="5">5+ baths</option>
                </select>
              </label>
            </div>
            <div className="listings-filters__actions">
              <div className="listings-filters__buttons">
                <button type="button" className="secondary-button ghost" onClick={() => setShowAdvanced((prev) => !prev)}>
                  {showAdvanced ? 'Done' : 'More'}
                </button>
                {isMobileFilters && (
                  <button
                    type="button"
                    className="secondary-button ghost"
                    onClick={() => setFiltersCollapsed((prev) => !prev)}
                  >
                    {filtersCollapsed ? 'Show filters' : 'Hide filters'}
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button ghost save-search-btn"
                  onClick={() => setSaveSearchOpen(true)}
                  title="Save this search to your Buyer Hub"
                >
                  <LuBell aria-hidden="true" /> Save search
                </button>
              </div>
            </div>
          </div>
          {meta.location && (
            <></>
          )}
        </div>

        {showAdvanced && (
          <>
            <div className="property-type-grid" role="group" aria-label="Property types">
              {propertyTypeOptions.map((type) => {
                const isActive = filterForm.propertyTypes?.includes(type)
                return (
                  <button
                    style={{ whiteSpace: 'nowrap' }}
                    type="button"
                    key={type}
                    className={`property-type-button ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      const current = filterForm.propertyTypes || []
                      const updated = isActive
                        ? current.filter((item) => item !== type)
                        : [...current, type]
                      updateFilterForm('propertyTypes', updated)
                    }}
                    aria-pressed={isActive}
                  >
                    {type === 'Terraced' ? (
                      <span className="property-type-cluster" aria-hidden="true">
                        <LuHouse />
                        <LuHouse className="mid" style={{ marginLeft: '-3px' }} />
                        <LuHouse style={{ marginLeft: '-3px' }} />
                      </span>
                    ) : (
                      (() => {
                        const Icon = propertyTypeIcons[type] || LuHouse
                        return <Icon aria-hidden="true" />
                      })()
                    )}
                    <span>{type}</span>
                  </button>
                )
              })}
            </div>
            <div className="listings-filters__toggles-row">
              <div className="listings-filters__toggles">
                <label className="toggle">
                  <input
                    type="checkbox"
                    className="toggle__input"
                    checked={filterForm.hasParking}
                    onChange={(event) => updateFilterForm('hasParking', event.target.checked)}
                  />
                  <span className="toggle__box" aria-hidden="true"></span>
                  <span className="toggle__text">Parking</span>
                </label>
                <label className="toggle">
                  <input
                    type="checkbox"
                    className="toggle__input"
                    checked={filterForm.hasGarden}
                    onChange={(event) => updateFilterForm('hasGarden', event.target.checked)}
                  />
                  <span className="toggle__box" aria-hidden="true"></span>
                  <span className="toggle__text">Garden</span>
                </label>
                <label className="toggle">
                  <input
                    type="checkbox"
                    className="toggle__input"
                    checked={filterForm.hasGarage}
                    onChange={(event) => updateFilterForm('hasGarage', event.target.checked)}
                  />
                  <span className="toggle__box" aria-hidden="true"></span>
                  <span className="toggle__text">Garage</span>
                </label>
              </div>
            </div>
            <details className="listings-filters__advanced" open>
              <summary>Advanced filters</summary>
              <div className="advanced-grid">
                <div className="advanced-section">
                  <p className="advanced-label">Tenure</p>
                  <div className="advanced-toggle-stack advanced-toggle-stack--row">
                    {tenureOptions.map((option) => {
                      const isActive = filterForm.tenure?.includes(option.value)
                      return (
                        <label key={option.value} className="toggle">
                          <input
                            type="checkbox"
                            className="toggle__input"
                            checked={isActive}
                            onChange={(event) => {
                              const current = filterForm.tenure || []
                              const updated = event.target.checked
                                ? [...current, option.value]
                                : current.filter((item) => item !== option.value)
                              updateFilterForm('tenure', updated)
                            }}
                          />
                          <span className="toggle__box" aria-hidden="true"></span>
                          <span className="toggle__text">{option.label}</span>
                        </label>
                      )
                    })}
                  </div>
                  <p className="advanced-label" style={{ marginTop: '0.5rem' }}>Status</p>
                  <div className="advanced-toggle-stack advanced-toggle-stack--row">
                    {statusFilters.map((filter) => {
                      const isActive = filterForm.statusFilters?.includes(filter.value)
                      return (
                        <label key={filter.key} className="toggle">
                          <input
                            type="checkbox"
                            className="toggle__input"
                            checked={isActive}
                            onChange={(event) => {
                              const current = filterForm.statusFilters || []
                              const updated = event.target.checked
                                ? [...current, filter.value]
                                : current.filter((item) => item !== filter.value)
                              updateFilterForm('statusFilters', updated)
                            }}
                          />
                          <span className="toggle__box" aria-hidden="true"></span>
                          <span className="toggle__text">{filter.label}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
                <div className="advanced-section">
                  <p className="advanced-label">Min EPC rating</p>
                  <select
                    aria-label="Min EPC rating"
                    value={filterForm.minEpcRating}
                    onChange={(event) => updateFilterForm('minEpcRating', event.target.value)}
                  >
                    <option value="">Any</option>
                    {epcRatingOptions.map((rating) => (
                      <option key={rating} value={rating}>{rating}</option>
                    ))}
                  </select>
                </div>
                <div className="advanced-section">
                  <p className="advanced-label">Max council tax band</p>
                  <select
                    aria-label="Max council tax band"
                    value={filterForm.maxCouncilTaxBand}
                    onChange={(event) => updateFilterForm('maxCouncilTaxBand', event.target.value)}
                  >
                    <option value="">Any</option>
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((band) => (
                      <option key={band} value={band}>Band {band}</option>
                    ))}
                  </select>
                </div>
                <div className="advanced-section">
                  <p className="advanced-label">Listing tags</p>
                  <div className="advanced-choice-stack">
                    {inclusionFilters.map((filter) => {
                      const isIncluded = Boolean(filterForm[filter.includeKey])
                      const isExcluded = Boolean(filterForm[filter.excludeKey])
                      return (
                        <div key={filter.id} className="advanced-choice-row">
                          <span className="advanced-choice-label">{filter.label}</span>
                          <div className="advanced-choice-actions" role="group" aria-label={`${filter.label} filter`}>
                            <button
                              type="button"
                              className={`advanced-choice-button ${isIncluded ? 'is-active' : ''}`}
                              aria-pressed={isIncluded}
                              onClick={() => updateFilterForm(filter.includeKey, !isIncluded)}
                            >
                              Only
                            </button>
                            <button
                              type="button"
                              className={`advanced-choice-button is-exclude ${isExcluded ? 'is-active' : ''}`}
                              aria-pressed={isExcluded}
                              onClick={() => updateFilterForm(filter.excludeKey, !isExcluded)}
                            >
                              Exclude
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </details>
          </>
        )}
      </form>

      {isMobileFilters && filtersCollapsed && (
        <button type="button" className="primary-button filters-fab" onClick={() => setFiltersCollapsed(false)}>
          Show filters
        </button>
      )}

      {error && (
        <div className="error-state">
          <p className="error-text">{error}</p>
          <button type="button" className="secondary-button" onClick={() => setRetryCount((c) => c + 1)}>
            Try again
          </button>
        </div>
      )}

      {viewMode === 'map' ? (
        results.length ? (
          <section className="listings-map" aria-label="Listings map">
            {loading && <div className="listings-map__loading">Updating map…</div>}
            <MapContainer center={mapCenter} zoom={mapZoom} maxZoom={19} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
              {meta.location?.radius && (
                <Circle
                  center={mapCenter}
                  radius={meta.location.radius * 1609.34}
                  pathOptions={{ color: '#3b82f6', weight: 1.5, fillColor: '#60a5fa', fillOpacity: 0.12 }}
                />
              )}
              <ClusteredMarkers
                mappedPositions={mappedPositions}
                listingLinkState={listingLinkState}
                isInComparison={isInComparison}
                addToComparison={addToComparison}
                removeFromComparison={removeFromComparison}
                isFull={isFull}
                isSaved={isSaved}
                onSave={handleListSaveToggle}
              />
            </MapContainer>
          </section>
        ) : loading ? (
          <LoadingIndicator label="Loading map…" />
        ) : (
          <>
            <p>No listings to plot.</p>
            {meta.total < 12 && renderSuggestions()}
          </>
        )
      ) : loading ? (
        <LoadingIndicator label="Loading listings…" />
      ) : results.length ? (
        viewMode === 'list' ? (
          <section className="listings-list" aria-label="List view">
            {resultsWithAd.map((item, index) =>
              item?.__ad ? (
                <ListingsAdPlaceholder key={item.id || `ad-${index}`} variant="list" partner={promotedPartners.length ? promotedPartners[item.partnerIndex % promotedPartners.length] : null} onLearnMore={handleAdLearnMore} />
              ) : (
                <article key={item.id} className="listings-list__item">
                  <Link
                    to={`/property/${item.id}`}
                    className="listings-list__media"
                    aria-label={`View details for ${item.title}`}
                    state={listingLinkState}
                  >
                    <img src={item.heroImage} alt={item.title} loading="lazy" />
                  </Link>
                  <div className="listings-list__content">
                    <div className="listings-list__row">
                      <p className="listings-list__price">
                        {formatPriceWithQualifier(item.price, item.priceQualifier)}
                      </p>
                      {item.hasAcceptedOffer && <span className="pill pill--warning">Under offer</span>}
                      <button
                        type="button"
                        className={`property-card__save ${isSaved(item.id) ? 'is-saved' : ''}`}
                        aria-label={isSaved(item.id) ? 'Remove from saved homes' : 'Save this property'}
                        onClick={(event) => handleListSaveToggle(event, item.id)}
                      >
                        <LuHeart aria-hidden="true" style={{ fill: isSaved(item.id) ? 'currentColor' : 'none' }} />
                      </button>
                    </div>
                    <h3>
                      <Link to={`/property/${item.id}`} state={listingLinkState}>
                        {item.title}
                      </Link>
                    </h3>
                    <p className="listings-list__location">
                      {[item.location, item.city].filter(Boolean).join(', ')}
                    </p>
                    <div className="listings-list__stats">
                      <span>
                        <LuBedDouble aria-hidden="true" /> {item.beds} beds
                      </span>
                      <span>
                        <LuBath aria-hidden="true" /> {item.baths} baths
                      </span>
                      {item.area ? (
                        <span>
                          <LuRuler aria-hidden="true" /> {item.area} sq ft
                        </span>
                      ) : null}
                    </div>
                    {item.description && (
                      <p className="listings-list__summary">
                        {item.description.length > 140 ? `${item.description.slice(0, 137)}...` : item.description}
                      </p>
                    )}
                    <div className="listings-list__actions">
                      <Link to={`/property/${item.id}`} state={listingLinkState}>
                        View details
                      </Link>
                      {!item.hasAcceptedOffer && (
                        <Link to={`/property/${item.id}#messages`} state={listingLinkState}>
                          Contact seller
                        </Link>
                      )}
                      <button
                        type="button"
                        className={`property-card__compare${isInComparison(item.id) ? ' is-comparing' : ''}`}
                        disabled={!isInComparison(item.id) && isFull}
                        onClick={() => isInComparison(item.id) ? removeFromComparison(item.id) : addToComparison(item.id)}
                      >
                        {isInComparison(item.id) ? 'Remove from compare' : isFull ? 'Comparison full' : 'Compare'}
                      </button>
                    </div>
                  </div>
                </article>
              ),
            )}
          </section>
        ) : (
          <section className="listings-grid" aria-label="Listings">
            {resultsWithAd.map((item, index) =>
              item?.__ad ? (
                <ListingsAdPlaceholder key={item.id || `ad-grid-${index}`} partner={promotedPartners.length ? promotedPartners[item.partnerIndex % promotedPartners.length] : null} onLearnMore={handleAdLearnMore} />
              ) : (
                <PropertyCard key={item.id} property={item} linkState={listingLinkState} showCompare />
              ),
            )}
          </section>
        )
      ) : (
        <div className="listings-empty-state">
          {hasActiveFilters ? (
            <>
              <h2>No homes match your search yet</h2>
              <p>
                Keyzee is a brand-new platform, so listings are still arriving — try broadening your search, or be
                the change and list your own home so other buyers can find it.
              </p>
            </>
          ) : (
            <>
              <h2>We're a brand-new platform</h2>
              <p>
                Keyzee has just launched, so there aren't any homes listed yet. That's good news if you're
                selling — be the first to list and get seen by early buyers with zero competition.
              </p>
            </>
          )}
          <button type="button" className="primary-button" onClick={() => navigate('/sell')}>
            Be the first to list your home
          </button>
          {meta.total < 12 && renderSuggestions()}
        </div>
      )}

      {!loading && results.length > 0 && meta.total < 12 && renderSuggestions()}

      {meta.totalPages > 1 && (
        <div className="pagination">
          <button
            type="button"
            className="secondary-button"
            onClick={() => handlePageChange(meta.page - 1)}
            disabled={meta.page <= 1}
          >
            Previous
          </button>
          <span>
            Page {meta.page} of {meta.totalPages}
          </span>
          <button
            type="button"
            className="secondary-button"
            onClick={() => handlePageChange(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
          >
            Next
          </button>
        </div>
      )}
    </section>

    {saveSearchOpen && (
      <div className="modal-backdrop" onClick={() => setSaveSearchOpen(false)} role="presentation">
        <div
          className="modal save-search-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-search-title"
        >
          <div className="modal__header">
            <h2 id="save-search-title">Save this search</h2>
            <button
              type="button"
              className="ghost-button icon-only"
              aria-label="Close"
              onClick={() => setSaveSearchOpen(false)}
            >
              <LuX aria-hidden="true" />
            </button>
          </div>
          <p>Name your search to find it again in your Buyer Hub.</p>
          <form onSubmit={handleSaveSearch}>
            <label>
              Search name
              <input
                type="text"
                value={saveSearchName}
                onChange={(e) => setSaveSearchName(e.target.value)}
                placeholder={filterForm.location ? `Homes in ${filterForm.location}` : 'My search'}
                autoFocus
              />
            </label>
            <div className="modal__actions">
              <button type="button" className="secondary-button ghost" onClick={() => setSaveSearchOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="primary-button" disabled={saveSearchStatus === 'saving'}>
                {saveSearchStatus === 'saving' ? 'Saving…' : 'Save search'}
              </button>
            </div>
            {saveSearchStatus === 'error' && (
              <p className="save-search-modal__error">Something went wrong — please try again.</p>
            )}
          </form>
        </div>
      </div>
    )}
    <PartnerAdvertModal
      partner={advertPartner}
      form={advertForm}
      onFormChange={handleAdvertFormChange}
      onSubmit={handleAdvertCallback}
      onClose={() => setAdvertPartner(null)}
      submitting={advertSubmitting}
      error={advertError}
      success={advertSuccess}
      successMessage={advertSuccessMessage}
    />
    </>
  )
}

export default ListingsPage
